/**
 * AirtimeTopUpPage — citizen airtime / data purchase.
 *
 * Live (service-financial-services, Spec 14):
 *   - POST /v1/wallet/wallets-mine/airtime-purchases
 *     Debits the citizen's wallet and credits the telco via the stub adapter.
 *     Requires Wallet/airtime-own (seeded by 20260515_citizen_airtime_kyc_perms.sql).
 *
 * Source: `screens/src/pages/nipost-pay/AirtimeTopUp.jsx` (mock-only).
 */
import { useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Phone, Wifi } from 'lucide-react';

import { purchaseAirtime, type AirtimeNetwork } from '../../lib/api/nipost-pay';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import {
  PageHeader,
  customerWalletQueryKey,
  describeWalletError,
  formatNaira,
} from './NipostPayUI';

interface NetworkOption {
  readonly id: AirtimeNetwork;
  readonly label: string;
  readonly color: string;
  readonly textColor: string;
}

const NETWORKS: readonly NetworkOption[] = [
  { id: 'MTN',     label: 'MTN',     color: 'bg-yellow-400', textColor: 'text-yellow-900' },
  { id: 'AIRTEL',  label: 'Airtel',  color: 'bg-red-500',    textColor: 'text-white' },
  { id: 'GLO',     label: 'Glo',     color: 'bg-green-500',  textColor: 'text-white' },
  { id: '9MOBILE', label: '9mobile', color: 'bg-teal-500',   textColor: 'text-white' },
];

const AIRTIME_AMOUNTS: readonly number[] = [100, 200, 500, 1000, 2000, 5000];

interface DataBundle {
  readonly size: string;
  readonly validity: string;
  readonly price: string;
}

const DATA_BUNDLES: Readonly<Record<AirtimeNetwork, readonly DataBundle[]>> = {
  MTN: [
    { size: '500MB',  validity: '1 day',   price: '100' },
    { size: '1GB',    validity: '30 days', price: '300' },
    { size: '3GB',    validity: '30 days', price: '700' },
    { size: '10GB',   validity: '30 days', price: '2000' },
  ],
  AIRTEL: [
    { size: '200MB',  validity: '1 day',   price: '100' },
    { size: '1GB',    validity: '30 days', price: '300' },
    { size: '5GB',    validity: '30 days', price: '1200' },
    { size: '15GB',   validity: '30 days', price: '2800' },
  ],
  GLO: [
    { size: '1GB',    validity: '30 days', price: '300' },
    { size: '5GB',    validity: '30 days', price: '1200' },
    { size: '15GB',   validity: '30 days', price: '2500' },
    { size: '30GB',   validity: '30 days', price: '4500' },
  ],
  '9MOBILE': [
    { size: '500MB',  validity: '30 days', price: '200' },
    { size: '1GB',    validity: '30 days', price: '300' },
    { size: '5GB',    validity: '30 days', price: '1200' },
    { size: '25GB',   validity: '30 days', price: '4500' },
  ],
};

type ProductType = 'airtime' | 'data';

export function AirtimeTopUpPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.upmpUserId ?? '';

  const [network, setNetwork] = useState<AirtimeNetwork>('MTN');
  const [productType, setProductType] = useState<ProductType>('airtime');
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [selectedBundle, setSelectedBundle] = useState<DataBundle | null>(null);
  const [msisdn, setMsisdn] = useState<string>('');

  const purchase = useMutation({
    mutationFn: () => {
      const amount =
        productType === 'data'
          ? selectedBundle?.price ?? '0'
          : selectedAmount !== null
            ? String(selectedAmount)
            : customAmount.length > 0
              ? customAmount
              : '0';
      return purchaseAirtime(
        {
          network,
          msisdn: msisdn.trim(),
          amount,
          currency: 'NGN',
          idempotencyKey: `airtime-${Date.now()}`,
        },
        { bearerToken: token ?? undefined },
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: customerWalletQueryKey(customerId) }),
  });

  function activeAmount(): string {
    if (productType === 'data') return selectedBundle?.price ?? '0';
    if (selectedAmount !== null) return String(selectedAmount);
    return customAmount.length > 0 ? customAmount : '0';
  }

   
  const ready = msisdn.length > 0 && Number(activeAmount()) > 0;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!ready) return;
    purchase.mutate();
  }

  return (
    <div>
      <PageHeader title="Airtime & data" description="Top up any Nigerian phone number from your NIPOST Pay wallet." />

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        noValidate
      >
        <div className="lg:col-span-2 bg-white rounded-xl border border-border p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Network</label>
            <div className="grid grid-cols-4 gap-3">
              {NETWORKS.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => {
                    setNetwork(n.id);
                    setSelectedBundle(null);
                  }}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-colors ${
                    network === n.id ? 'border-nipost-green' : 'border-border hover:border-gray-300'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full ${n.color} flex items-center justify-center`}>
                    <span className={`text-xs font-bold ${n.textColor}`}>{n.label}</span>
                  </div>
                  <span className="text-xs font-medium text-gray-700">{n.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product</label>
            <div className="flex gap-2">
              {(
                [
                  ['airtime', 'Airtime',     Phone],
                  ['data',    'Data bundle', Wifi],
                ] as const
              ).map(([val, label, Icon]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => {
                    setProductType(val);
                    setSelectedAmount(null);
                    setSelectedBundle(null);
                    setCustomAmount('');
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border ${
                    productType === val
                      ? 'bg-nipost-green text-white border-nipost-green'
                      : 'border-border text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
          </div>

          {productType === 'airtime' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
                {AIRTIME_AMOUNTS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => {
                      setSelectedAmount(a);
                      setCustomAmount('');
                    }}
                    className={`py-2 text-sm font-medium rounded-lg border ${
                      selectedAmount === a
                        ? 'bg-nipost-green text-white border-nipost-green'
                        : 'border-border hover:bg-gray-50'
                    }`}
                  >
                    ₦{a.toLocaleString()}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min="50"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setSelectedAmount(null);
                }}
                placeholder="Custom amount"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data bundle</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {DATA_BUNDLES[network].map((b) => (
                  <button
                    key={b.size}
                    type="button"
                    onClick={() => setSelectedBundle(b)}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      selectedBundle?.size === b.size
                        ? 'border-nipost-green bg-green-50'
                        : 'border-border hover:border-gray-300'
                    }`}
                  >
                    <p className="font-bold text-gray-900">{b.size}</p>
                    <p className="text-xs text-muted">{b.validity}</p>
                    <p className="text-sm font-semibold text-nipost-green mt-1">
                      {formatNaira(b.price)}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label htmlFor="msisdn" className="block text-sm font-medium text-gray-700 mb-1">
              Phone number
            </label>
            <input
              id="msisdn"
              value={msisdn}
              onChange={(e) => setMsisdn(e.target.value)}
              placeholder="+2348012345678"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
            />
          </div>

          {purchase.isError ? (
            <div
              className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700 flex items-center gap-2"
              role="alert"
            >
              {describeWalletError(purchase.error)}
            </div>
          ) : null}

          {purchase.isSuccess ? (
            <div
              className="rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2"
              role="status"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Top-up successful — new balance{' '}
              {formatNaira(purchase.data.walletBalance)}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={!ready || purchase.isPending}
            className="w-full py-3 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {purchase.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
            Buy now — {formatNaira(activeAmount())}
          </button>
        </div>

        <div>
          <div className="bg-white rounded-xl border border-border p-5 lg:sticky lg:top-24">
            <h3 className="font-semibold text-gray-900 mb-3">Summary</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted">Network</dt>
                <dd className="font-medium">{network}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">Product</dt>
                <dd className="font-medium capitalize">
                  {productType === 'data' && selectedBundle ? `${selectedBundle.size} bundle` : productType}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">Recipient</dt>
                <dd className="font-medium">{msisdn || '—'}</dd>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-100">
                <dt className="text-muted">Amount</dt>
                <dd className="font-semibold text-nipost-green">{formatNaira(activeAmount())}</dd>
              </div>
            </dl>
          </div>
        </div>
      </form>
    </div>
  );
}
